import { initializeApp } from "firebase/app";

import {
  getAuth,
  signInWithRedirect,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  NextOrObserver,
} from "firebase/auth";

import {
  doc,
  getDoc,
  getFirestore,
  QueryDocumentSnapshot,
  setDoc,
} from "firebase/firestore";

import { connectFunctionsEmulator, getFunctions, httpsCallable } from "firebase/functions";

import { getStorage } from "firebase/storage";

import { getOrFetchOnlineOrderingSessionToken } from "../onlineOrdering/surchargeConfigs";
import { firebaseConfig } from "./configKeys";
import { getAnalytics } from "firebase/analytics";
import { Order } from "../../store/order/order.types";

// Initialize Firebase
const firebaseApp = initializeApp(firebaseConfig);

const googleProvider = new GoogleAuthProvider();

googleProvider.setCustomParameters({
  prompt: "select_account",
});

/** Port for `firebase emulators:start --only functions` (override with REACT_APP_FUNCTIONS_EMULATOR_PORT). */
export const FUNCTIONS_EMULATOR_PORT = (() => {
  const p = parseInt(
    process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || "5001",
    10,
  );
  return Number.isFinite(p) && p > 0 ? p : 5001;
})();

export const functions = getFunctions(firebaseApp, "australia-southeast1");

if (process.env.REACT_APP_ENV === "development") {
  connectFunctionsEmulator(functions, "127.0.0.1", FUNCTIONS_EMULATOR_PORT);
}

export const storage = getStorage(firebaseApp);

export const auth = getAuth();

export const analytics = getAnalytics(firebaseApp);

export const signInWithGooglePopup = () =>
  signInWithPopup(auth, googleProvider);

export const signInWithGoogleRedirect = () =>
  signInWithRedirect(auth, googleProvider);

export const db = getFirestore();

export type AdditionalInformation = {
  displayName?: string;
};

export type UserData = {
  createdAt: Date;
  displayName: string;
  email: string;
};

export const createUserDocumentFromAuth = async (
  userAuth: User,
  additionalInformation = {} as AdditionalInformation
): Promise<void | QueryDocumentSnapshot<UserData>> => {
  if (!userAuth) return;
  const userDocRef = doc(db, "users", userAuth.uid);

  const userSnapshot = await getDoc(userDocRef);
  if (!userSnapshot.exists()) {
    const { displayName, email } = userAuth;
    const createdAt = new Date();

    try {
      await setDoc(userDocRef, {
        displayName,
        email,
        createdAt,
        ...additionalInformation,
      });
    } catch (error) {
      console.log("error creating the user", error);
    }
  }

  return userSnapshot as QueryDocumentSnapshot<UserData>;
};

export const createAuthUserWithEmailAndPassword = async (
  email: string,
  password: string
) => {
  if (!email || !password) return;

  return await createUserWithEmailAndPassword(auth, email, password);
};

/**
 * Creates a pending order in `orders` via Cloud Function.
 * Sanitization and scheduled-time normalisation happen server-side.
 */
export const createOnlineOrder = async (order: Order): Promise<string> => {
  const kitchenId = order?.kitchenId ?? "";
  const { sessionToken } = await getOrFetchOnlineOrderingSessionToken(kitchenId);
  const fn = httpsCallable<
    { order: Order; kitchenId: string; sessionToken: string },
    { orderId: string }
  >(functions, "onlineOrdering-createOrder");
  const { data } = await fn({ order, kitchenId, sessionToken });
  return data.orderId;
};

export type OrderConfirmationStatus = {
  paymentStatus: string | null;
  orderCount: string | null;
  orderStatus: string | null;
  orderPayedAt: unknown;
  customerAccountId: string | null;
  customerAccountEmail: string | null;
  customerAccountMobileNumber: string | null;
  kitchenId: string;
};

/** Waits server-side for Tyro webhook finalization, then returns confirmation fields. */
export const getOrderConfirmation = async (params: {
  orderId?: string | null;
  tyroPayRequestId?: string | null;
  kitchenId: string;
}): Promise<OrderConfirmationStatus> => {
  const { orderId, tyroPayRequestId, kitchenId } = params;
  const { sessionToken } = await getOrFetchOnlineOrderingSessionToken(kitchenId);
  const fn = httpsCallable<
    {
      orderId?: string | null;
      tyroPayRequestId?: string | null;
      kitchenId: string;
      sessionToken: string;
    },
    OrderConfirmationStatus
  >(functions, "onlineOrdering-getOrderConfirmation");
  const { data } = await fn({ orderId, tyroPayRequestId, kitchenId, sessionToken });
  return data;
};